import { useMutation } from "@apollo/client";
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Link,
  Paper,
  Stack,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import React, { useState } from "react";

import { BULK_INVITE_WITH_USER_IDS } from "../gql/mutations/event";
import { SHARE_EVENT_TO_USER_IDS } from "../gql/queries/shareEvent";
import {
  cleanTitle,
  FIRST_EVENT_LABELS,
  firstEventKinds,
  type NewMember,
  type NewMemberEvent,
} from "../helpers/newMembers";
import ResponsiveTable from "./ResponsiveTable";

interface Props {
  member: NewMember;
  onClose: () => void;
}

/** A term and its value, in the two answer columns. */
const Fact: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box sx={{ mb: 1 }}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body2" component="div">
      {children}
    </Typography>
  </Box>
);

const whenText = (event: NewMemberEvent): string => {
  if (!event.when) {
    return `${new Date(event.startDate).toUTCString().slice(0, 22)} UTC (no time zone for her)`;
  }
  return `${event.when} her time${event.clock === "signup" ? " (the time zone she picked at signup)" : ""}`;
};

/**
 * One new member, in full: what she told us at signup, what she has done since,
 * and the event each invitation queue would send her — with share and invite
 * right there, so acting on it doesn't mean leaving the list.
 *
 * The picks are the same rules Invite Campaigns plans with, so the event
 * suggested here and the one the campaign would invite her to never disagree.
 */
const NewMemberDetailPanel: React.FC<Props> = ({ member, onClose }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [busyEventId, setBusyEventId] = useState("");
  const [share] = useMutation(SHARE_EVENT_TO_USER_IDS);
  const [invite] = useMutation(BULK_INVITE_WITH_USER_IDS);

  const { suggestion } = member;
  const inMessage = suggestion.invitation ? null : suggestion.event?.eventId;

  const onShare = async (event: NewMemberEvent): Promise<void> => {
    setBusyEventId(event.eventId);
    try {
      const { data } = await share({
        variables: { eventId: event.eventId, userIds: [member.userId] },
      });
      const shared = data?.adminShareEventToUserIds?.sharedCount ?? 0;
      enqueueSnackbar(
        shared > 0
          ? `Shared “${cleanTitle(event.title)}” with ${member.firstName}.`
          : `Couldn't share with ${member.firstName}.`,
        { variant: shared > 0 ? "success" : "warning" },
      );
    } catch (caught) {
      enqueueSnackbar(caught instanceof Error ? caught.message : "Share failed.", {
        variant: "error",
      });
    } finally {
      setBusyEventId("");
    }
  };

  const onInvite = async (event: NewMemberEvent): Promise<void> => {
    if (
      !window.confirm(
        `Invite ${member.firstName} to “${cleanTitle(event.title)}”? This books her a place.`,
      )
    ) {
      return;
    }
    setBusyEventId(event.eventId);
    try {
      const { data } = await invite({
        variables: { eventId: event.eventId, input: { userIds: [member.userId] } },
      });
      const invited: string[] = data?.bulkInviteWithUserIds?.invitedUserIds ?? [];
      const warnings: string[] = data?.bulkInviteWithUserIds?.warningMessages ?? [];
      if (invited.length > 0) {
        enqueueSnackbar(`Invited ${member.firstName} to “${cleanTitle(event.title)}”.`, {
          variant: "success",
        });
      } else {
        enqueueSnackbar(warnings.join(" ") || `${member.firstName} wasn't invited.`, {
          variant: "info",
        });
      }
    } catch (caught) {
      enqueueSnackbar(caught instanceof Error ? caught.message : "Invite failed.", {
        variant: "error",
      });
    } finally {
      setBusyEventId("");
    }
  };

  const firstEvent = firstEventKinds(member);
  const noClock = !member.timezone && !member.selectedTimezone;

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
      <Stack spacing={1.5} sx={{ minWidth: 0 }}>
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
            📨 {member.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {member.email}
          </Typography>
          <IconButton size="small" onClick={onClose} aria-label="Close">
            ✕
          </IconButton>
        </Box>

        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          <Chip size="small" label={member.statusLabel} />
          {member.status === "guest" && member.guestTokensLeft != null && (
            <Chip
              size="small"
              variant="outlined"
              label={`${member.guestTokensLeft} of 3 pass events left${
                member.guestDaysLeft != null
                  ? ` · ${member.guestDaysLeft} day${member.guestDaysLeft === 1 ? "" : "s"} to go`
                  : ""
              }`}
            />
          )}
          {member.speakingLevel && (
            <Chip
              size="small"
              variant="outlined"
              label={member.speakingLevel.replace(/([a-z])([A-Z])/g, "$1 $2")}
            />
          )}
          <Chip
            size="small"
            variant="outlined"
            label={
              member.timezone
                ? member.timezone
                : `No time zone on her profile${member.selectedTimezone ? `; picked ${member.selectedTimezone} at signup` : ""}`
            }
          />
        </Stack>

        {member.redFlagLabel && (
          <Alert severity="error">
            <strong>Red flag.</strong> {member.redFlagLabel}
          </Alert>
        )}
        {member.status === "paused" && (
          <Alert severity="info">
            Her membership is paused. The first message angle acknowledges that and doesn't push
            events.
          </Alert>
        )}
        {member.status === "guest" && member.guestTokensLeft === 0 && (
          <Alert severity="info">
            She has used all 3 Guest Pass events. No invitation opens a fourth.
          </Alert>
        )}
        {noClock && (
          <Alert severity="warning">
            We don't know her time zone, so no time is put in her message.
          </Alert>
        )}

        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" fontWeight={600} display="block" mb={1}>
              Her signup answers
            </Typography>
            <Fact label="Wants spoken English">{member.priorityLabel ?? "No answer"}</Fact>
            <Fact label="What makes practising hard">{member.challengeLabel ?? "No answer"}</Fact>
            <Fact label="Time to practise this week">{member.readinessLabel ?? "No answer"}</Fact>
            <Fact label="First event she picked">
              {member.firstEventSkipped ? (
                <Chip size="small" color="error" label="Skipped booking one" />
              ) : member.firstEventId ? (
                <>
                  {cleanTitle(member.firstEventTitle) || "(event no longer listed)"}{" "}
                  {firstEvent.slice(1).map((kind) => (
                    <Chip
                      key={kind}
                      size="small"
                      color={kind === "came" ? "success" : kind === "missed" ? "error" : "default"}
                      label={FIRST_EVENT_LABELS[kind]}
                    />
                  ))}
                  {member.firstEventWhen && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {member.firstEventWhen} her time
                    </Typography>
                  )}
                </>
              ) : (
                "No answer"
              )}
            </Fact>
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" fontWeight={600} display="block" mb={1}>
              What she has done since
            </Typography>
            <Fact label="Events she has come to">{member.eventsAttended}</Fact>
            <Fact label="Said yes to, still ahead">{member.acceptedUpcoming}</Fact>
            <Fact label="Upcoming invitations and shares she holds">{suggestion.holds}</Fact>
            <Fact label="Types she hasn't been to yet">
              {suggestion.untried.length > 0
                ? suggestion.byType
                    .filter((type) => suggestion.untried.includes(type.programme))
                    .map((type) => type.label)
                    .join(", ")
                : "She has been to all three"}
            </Fact>
          </Box>
        </Box>

        <Box>
          <Typography variant="caption" fontWeight={600} display="block">
            What the invitation rules would send her
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            One pick per type, by the same rules as Invite Campaigns: an event she can open, in her
            own 7am to 10pm, inside her Guest Pass, at her level, not one she said no to, soonest
            first.
          </Typography>
        </Box>

        {suggestion.invitation && (
          <Alert severity="info">
            She already holds an invitation she can open:{" "}
            <Link href={suggestion.invitation.link} target="_blank" rel="noopener">
              {cleanTitle(suggestion.invitation.title)}
            </Link>
            {suggestion.invitation.when ? `, ${suggestion.invitation.when} her time` : ""}.
            Invitations come first, so her message points her to her Invitations rather than a new
            event.
          </Alert>
        )}

        <ResponsiveTable minWidth={640} maxHeight="min(360px, 45vh)">
          <TableHead>
            <TableRow>
              <TableCell>Type</TableCell>
              <TableCell>Event</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {suggestion.byType.map((type) => {
              const event = type.event;
              return (
                <TableRow
                  key={type.programme}
                  hover
                  selected={Boolean(event && event.eventId === inMessage)}
                >
                  <TableCell>
                    <Typography variant="body2">{type.label}</Typography>
                    {type.tried && (
                      <Chip size="small" color="success" variant="outlined" label="been to one" />
                    )}
                  </TableCell>
                  <TableCell>
                    {event ? (
                      <>
                        <Link
                          href={event.link}
                          target="_blank"
                          rel="noopener"
                          variant="body2"
                          fontWeight={600}
                        >
                          {cleanTitle(event.title) || event.title}
                        </Link>{" "}
                        <Stack
                          direction="row"
                          spacing={0.5}
                          display="inline-flex"
                          flexWrap="wrap"
                          useFlexGap
                        >
                          {event.eventId === inMessage && (
                            <Chip size="small" color="success" label="in her message" />
                          )}
                          {event.afterPass && (
                            <Chip size="small" color="warning" label="after her pass ends" />
                          )}
                          {event.outsideHerDay && (
                            <Chip size="small" color="warning" label="outside 7am–10pm her time" />
                          )}
                          {event.levelMismatch && (
                            <Chip size="small" color="warning" label="not her level" />
                          )}
                        </Stack>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {whenText(event)}
                          {event.hostName ? ` · ${event.hostName}` : ""}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Why: {event.why || "the soonest one she can open"}
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        Nothing she can open in the next 3 weeks.
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {event && (
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={busyEventId !== ""}
                          onClick={() => void onShare(event)}
                        >
                          Share
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={busyEventId !== ""}
                          onClick={() => void onInvite(event)}
                        >
                          Invite
                        </Button>
                      </Stack>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </ResponsiveTable>

        <Typography variant="caption" color="text.secondary">
          The library page for her answers:{" "}
          <Link href={suggestion.resourceLink} target="_blank" rel="noopener">
            {suggestion.resourceTitle}
          </Link>
        </Typography>
      </Stack>
    </Paper>
  );
};

export default NewMemberDetailPanel;
